import { StrictMode, type ComponentType } from 'react';
import { createRoot } from 'react-dom/client';
// Stylesheet order is load-bearing and is the reason these imports live here
// rather than in each app's entry point. ES module evaluation follows import
// order, and this module is imported *before* the app's `App.js`, so the token
// layer and the reset are in the document before any component stylesheet —
// every token reference resolves on first paint instead of falling back.
//
// ⚠️ This line used to write out a CSS custom-property reference as an EXAMPLE, and `lint:tokens` —
// widened on 2026-08-05 to cover this package — read the example as a real reference to a property that
// does not exist. Reworded to describe rather than demonstrate, instead of teaching the lint to tell
// prose from code: it is a regex scan by design, and a parser able to make that distinction is a much
// larger thing to trust with the same job. ⚠️ The first attempt at this note quoted the offending text
// and tripped the same rule, which is worth recording: an explanation of a lint violation must not
// contain one.
import '@dedupr/tokens/css';
import './base.css';
import { ThemeProvider } from './theme.js';
import { startMocks, unregisterStaleMockWorker, type MockSurface } from './mocks.js';

/**
 * Mount a Dedupr SPA.
 *
 * Every app's entry point was byte-identical apart from which `App` it
 * rendered, so the ordering rules below were duplicated per app — and a rule
 * restated in three places is a rule that will eventually differ in one of
 * them. Each app's `main.tsx` is now two lines that cannot get this wrong.
 */
export interface BootstrapOptions {
  /**
   * Set false when the app is pointed at a REAL backend, so MSW never starts.
   *
   * `MOCKS_ENABLED` alone is not enough: it is opt-*out* (`VITE_USE_MOCKS !==
   * 'false'`), so an app configured with real credentials but without that second
   * variable would start a mock service worker anyway. That combination is the
   * dangerous one — the operator console rendered real client names with a "MOCK
   * DATA" switcher over them, which is worse than either mode alone because it
   * makes the honest label a lie.
   *
   * An app that knows it has a backend must be able to say so in one place
   * instead of relying on two env vars agreeing.
   *
   * Complements, and does not replace, `scripts/vite-mock-guard.mjs`: this
   * option governs a *configured* app's behaviour, the build guard governs what
   * is in the artifact at all. See the note in `mocks.ts` for why the runtime
   * layer alone was not enough.
   */
  mocks?: boolean;
}

export function bootstrap(
  App: ComponentType,
  surface: MockSurface = 'tenant',
  options: BootstrapOptions = {}
): void {
  const root = document.getElementById('root');
  if (!root) throw new Error('#root not found in index.html');

  // Mocks must be listening *before* React mounts, or the first request escapes
  // the service worker and fails against a backend that does not exist yet —
  // which looks like a broken app rather than a race. `.finally` not `.then`:
  // if mock startup fails we still mount, and the app's own error states
  // report the failed requests honestly.
  const wantMocks = options.mocks ?? true;
  /**
   * When mocks are OFF, still clear a mock worker a previous mock-mode session
   * registered — `startMocks` is not called on this branch, so its own cleanup
   * would never run. A service worker outlives the page that registered it, and
   * this app has already shipped one instance of real client names appearing under
   * a mock affordance; a leftover worker controlling a live origin is the same
   * class of problem waiting for an MSW release that stops passing through.
   */
  const ready = wantMocks ? startMocks(surface) : unregisterStaleMockWorker();
  ready.finally(() => {
    createRoot(root).render(
      <StrictMode>
        {/* orgDefault is undefined until F26's setup journey writes it to the
            control plane (ADR-051); resolveTheme falls through to the product
            default, which is the documented behaviour, not a stub. */}
        <ThemeProvider>
          <App />
        </ThemeProvider>
      </StrictMode>
    );
  });
}
